import { SentryError } from '@ifixit/sentry';
import debounce from 'lodash/debounce';
import type { AnalyticsItem, AnalyticsItemsEvent, CustomAnalyticsEvent, GrowthbookEvent } from '..';

type GA4GrowthbookEvent = {
   eventName: string;
} & GrowthbookEvent;

type GTagViewItemsListEvent = {
   items: AnalyticsItem[];
   item_list_id: string;
   item_list_name: string;
};

type GA4InitialState = {
   page_referrer: string;
   page_location: string;
   page_title: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GTagArg = Array<any>;

export type GTag = (...args: GTagArg) => void;
declare global {
   interface Window {
      gtag?: GTag;
      dataLayer?: GTagArg;
      __ga4_initial_state?: GA4InitialState;
   }
}

let DEBUG_MODE = false;

if (typeof window !== 'undefined') {
   DEBUG_MODE = window.location.search.includes('ga4_debug=true');

   window.dataLayer = window.dataLayer || [];
   window.gtag = windowGtag;
   window.gtag('js', new Date());
}

function gtag(...args: GTagArg) {
   if (typeof window === 'undefined') {
      return;
   }

   if (!window.gtag) {
      throw new SentryError('window.gtag is not defined');
   }

   if (DEBUG_MODE) {
      // eslint-disable-next-line no-console
      console.log('[GA4] gtag', ...args);
   }

   window.gtag(...args);
}

function windowGtag() {
   // eslint-disable-next-line prefer-rest-params
   window?.dataLayer?.push(arguments);
}

export function initGA4ArrayOnWindow({
   GtagID,
   debugMode,
}: { GtagID: string | undefined; debugMode: boolean }) {
   if (typeof window === 'undefined' || !GtagID || !window.gtag) {
      return;
   }

   if (debugMode) {
      DEBUG_MODE = true;
   }

   const config: Record<string, unknown> = DEBUG_MODE ? { debug_mode: true } : {};

   // Use captured state if available (defensive against timing issues)
   // This preserves accurate attribution even if SPA navigation occurs before gtag('config')
   if (window.__ga4_initial_state) {
      config.page_referrer = window.__ga4_initial_state.page_referrer;
      config.page_location = window.__ga4_initial_state.page_location;
      config.page_title = window.__ga4_initial_state.page_title;
   }

   const mobileClientId = new URLSearchParams(window.location.search).get('_ga');
   if (mobileClientId) {
      config.client_id = mobileClientId;
   }

   gtag('config', GtagID, config);
}

export function setGA4Consent({ consent, type }: { consent: boolean; type: 'default' | 'update' }) {
   const consentValue = consent ? 'granted' : 'denied';
   gtag('consent', type, {
      ad_storage: consentValue,
      ad_user_data: consentValue,
      ad_personalization: consentValue,
      analytics_storage: consentValue,
   });
   gtag('event', `consent_${type}`, { consentValue });
}

export function trackGA4ViewItem(event: AnalyticsItemsEvent) {
   gtag('event', 'view_item', formatEvent(event));
}

export function trackGA4ViewCart(event: AnalyticsItemsEvent) {
   gtag('event', 'view_cart', formatEvent(event));
}

export function trackGA4AddToCart(event: AnalyticsItemsEvent) {
   gtag('event', 'add_to_cart', formatEvent(event));
}

export function trackGA4RemoveFromCart(event: AnalyticsItemsEvent) {
   gtag('event', 'remove_from_cart', formatEvent(event));
}

export function trackGA4ViewItemList(event: GTagViewItemsListEvent) {
   gtag('event', 'view_item_list', event);
}

export const debouncedTrackGA4ViewItemList: GTag = debounce((event: GTagViewItemsListEvent) => {
   trackGA4ViewItemList(event);
}, 500);

function formatEvent(event: AnalyticsItemsEvent) {
   const { items, ...rest } = event;
   const formatedItems = items.map(item => {
      const { categories, ...rest } = item;
      return {
         ...rest,
      };
   });
   return {
      ...rest,
      items: formatedItems,
   };
}

export const trackGA4GrowthbookEvent = (trackData: GA4GrowthbookEvent) => {
   gtag('event', trackData.eventName, {
      'experiment_id': trackData.experimentId,
      'variation_id': trackData.variationId,
   });
};

export function trackCustomGAEvent({ eventAction, ...eventParams }: CustomAnalyticsEvent) {
   // The first arg is called "eventName" in GA4, but its conceptually closer to
   // the eventAction value in our piwik values. So here its sent as the first arg.
   // Also, "eventName" in ga4 has to be 40 chars or less
   const trimmedEventName = trimParam(eventAction, 40);
   // All params must be under 100 chars
   const trimmedParams = Object.keys(eventParams).reduce(
      (acc, key: string) => {
         if (key in eventParams) {
            const typedKey = key as keyof typeof eventParams;
            if (typeof eventParams[typedKey] === 'string') {
               acc[key] = trimParam(eventParams[typedKey] as string, 100);
            } else if (typeof eventParams[typedKey] === 'number') {
               acc[key] = eventParams[typedKey] as number;
            }
         }
         return acc;
      },
      {} as { [key: string]: string | number }
   );
   gtag('event', trimmedEventName, trimmedParams);
}

const trimParam = (param: string, maxLength: number): string =>
   param.length > maxLength ? param.slice(0, maxLength - 3) + '...' : param;

// GA4 equivalents for Piwik custom dimensions - tracked as custom events
export function trackGA4PreferredStore(storeDomain: string) {
   gtag('event', 'set_preferred_store', { store_domain: storeDomain });
}

export function trackGA4PreferredLanguage(languageCode: string) {
   gtag('event', 'set_preferred_language', { language_code: languageCode.toUpperCase() });
}

export function trackGA4PreferredLocale(localeCode: string) {
   gtag('event', 'set_preferred_locale', { locale_code: localeCode });
}

export function trackGA4UserPrivilege(userPrivilege: string | null) {
   gtag('event', 'set_user_privilege', { user_privilege: userPrivilege });
}

export function trackGA4VisitedStore() {
   gtag('event', 'visited_store');
}

export function trackGA4PageBreadcrumbs(breadcrumbs: string) {
   gtag('event', 'page_breadcrumbs', { breadcrumbs: trimParam(breadcrumbs, 100) });
}

export function trackGA4FeaturedProducts(productSKUs: string[]) {
   gtag('event', 'featured_products', { product_skus: productSKUs.join(',').slice(0, 100) });
}

export function trackGA4CartUpdate(event: AnalyticsItemsEvent) {
   gtag('event', 'cart_update', formatEvent(event));
}
